import React, { useState, useEffect } from 'react';
import { 
  Database, Download, Upload, Trash2, RefreshCw, 
  ChevronLeft, Clock, User, HardDrive, AlertTriangle,
  CheckCircle2, FileArchive, Shield
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BackupManagement = ({ onBack, user }) => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const token = localStorage.getItem('auth_token');

  // Fetch backups
  const fetchBackups = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/backup/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch backups');
      const data = await res.json();
      setBackups(data.backups || []);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load backups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBackups();
  }, []);

  // Create backup
  const createBackup = async () => {
    try {
      setCreating(true);
      const res = await fetch(`${API_URL}/api/admin/backup/create`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Backup failed');
      }
      
      const data = await res.json();
      toast.success('Backup created successfully!');
      fetchBackups();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  // Download backup
  const downloadBackup = async (backupId, filename) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/backup/download/${backupId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Download failed');
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Download started!');
    } catch (error) {
      toast.error('Failed to download backup');
    }
  };

  // Restore backup
  const restoreBackup = async (backupId) => {
    try {
      setRestoring(backupId);
      const res = await fetch(`${API_URL}/api/admin/backup/restore`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ backup_id: backupId, confirm: true })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Restore failed');
      }
      
      toast.success('Database restored successfully! Page will refresh...');
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRestoring(null);
      setConfirmRestore(null);
    }
  };

  // Delete backup
  const deleteBackup = async (backupId) => {
    if (!confirm('Are you sure you want to delete this backup?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/backup/${backupId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Delete failed');
      
      toast.success('Backup deleted');
      fetchBackups();
    } catch (error) {
      toast.error('Failed to delete backup');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  // Format file size
  const formatSize = (sizeMb) => {
    if (sizeMb < 1) return `${Math.round(sizeMb * 1024)} KB`;
    return `${sizeMb.toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#0a1628]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="backup-management">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} className="p-2">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#0a1628] flex items-center gap-2">
              <Database className="w-7 h-7" />
              System Backup
            </h1>
            <p className="text-gray-500">Create and manage database backups</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchBackups} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          <Button 
            onClick={createBackup} 
            disabled={creating}
            className="bg-[#0a1628] gap-2"
          >
            {creating ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Database className="w-4 h-4" />
            )}
            {creating ? 'Creating...' : 'Create Backup'}
          </Button>
        </div>
      </div>

      {/* Warning Card */}
      <Card className="p-4 mb-6 bg-amber-50 border-amber-200">
        <div className="flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-800">Important</h3>
            <p className="text-sm text-amber-700">
              Restoring a backup will <strong>replace all current data</strong> with the backup data.
              This action cannot be undone. Always create a new backup before restoring.
            </p>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FileArchive className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{backups.length}</p>
              <p className="text-sm text-gray-500">Total Backups</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <HardDrive className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {formatSize(backups.reduce((acc, b) => acc + (b.size_mb || 0), 0))}
              </p>
              <p className="text-sm text-gray-500">Total Size</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <Clock className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {backups[0] ? new Date(backups[0].created_at).toLocaleDateString() : 'Never'}
              </p>
              <p className="text-sm text-gray-500">Latest Backup</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Backups List */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-[#0a1628]">Available Backups</h2>
        
        {backups.length === 0 ? (
          <Card className="p-8 text-center">
            <Database className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No backups yet</p>
            <p className="text-sm text-gray-400 mt-1">Create your first backup to protect your data</p>
          </Card>
        ) : (
          backups.map((backup) => (
            <Card key={backup.id} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#0a1628]/10 rounded-lg flex items-center justify-center">
                    <FileArchive className="w-6 h-6 text-[#0a1628]" />
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{backup.filename}</h3>
                      {backup.status === 'orphan' && (
                        <Badge variant="outline" className="text-yellow-600 border-yellow-400">
                          Orphan
                        </Badge>
                      )}
                    </div>
                    
                    <div className="text-sm text-gray-500 space-y-1">
                      <p className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(backup.created_at)}
                      </p>
                      <p className="flex items-center gap-1">
                        <HardDrive className="w-4 h-4" />
                        {formatSize(backup.size_mb)}
                        {backup.collections?.length > 0 && (
                          <span className="ml-2">• {backup.collections.length} collections</span>
                        )}
                      </p>
                      {backup.created_by_name && (
                        <p className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          Created by {backup.created_by_name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadBackup(backup.id, backup.filename)}
                    className="gap-1"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </Button>
                  
                  {confirmRestore === backup.id ? (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => restoreBackup(backup.id)}
                        disabled={restoring === backup.id}
                        className="gap-1"
                      >
                        {restoring === backup.id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4" />
                        )}
                        Confirm
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmRestore(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmRestore(backup.id)}
                      className="gap-1 text-orange-600 border-orange-300 hover:bg-orange-50"
                    >
                      <Upload className="w-4 h-4" />
                      Restore
                    </Button>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteBackup(backup.id)}
                    className="text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Security Notice */}
      <Card className="mt-6 p-4 bg-gray-50">
        <div className="flex gap-3">
          <Shield className="w-5 h-5 text-gray-600 flex-shrink-0" />
          <div className="text-sm text-gray-600">
            <p className="font-medium">Security Notice</p>
            <p>Backups contain all database data including user information. Keep downloaded backups secure and delete when no longer needed.</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default BackupManagement;
